import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import api from '../api/axios'
import toast from 'react-hot-toast'
import { AlertTriangle, CheckCircle, ClipboardList } from 'lucide-react'

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

function FieldError({ errors, name }: { errors: Record<string, string>, name: string }) {
  if (!errors[name]) return null
  return <p className="text-xs text-red-500 mt-1">{errors[name]}</p>
}

export default function OnboardingFormPage() {
  const { token } = useParams()
  const [linkInfo, setLinkInfo] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sameAddress, setSameAddress] = useState(false)

  const [form, setForm] = useState({
    fullName: '', dateOfBirth: '', gender: '', bloodGroup: '',
    dateOfJoining: '', aadhaarNumber: '', phoneNumber: '', altPhoneNumber: '',
    personalEmail: '', emergencyContactName: '', emergencyRelationship: '',
    emergencyContactPhone: '', permanentAddress: '', contactAddress: '',
    bankAccountNumber: '', bankIfscCode: '', bankBranchName: ''
  })

  const set = (key: string, value: string) => {
    setForm(f => ({ ...f, [key]: value }))
    if (value.trim()) setFieldErrors(fe => ({ ...fe, [key]: '' }))
  }

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const requiredFields: Record<string, string> = {
    fullName: 'Full name is required',
    dateOfBirth: 'Date of birth is required',
    gender: 'Gender is required',
    dateOfJoining: 'Date of joining is required',
    aadhaarNumber: 'Aadhaar number is required',
    phoneNumber: 'Phone number is required',
    personalEmail: 'Email is required',
    emergencyContactName: 'Emergency contact name is required',
    emergencyRelationship: 'Relationship is required',
    emergencyContactPhone: 'Emergency contact phone is required',
    permanentAddress: 'Permanent address is required',
    bankAccountNumber: 'Bank account number is required',
    bankIfscCode: 'IFSC code is required',
    bankBranchName: 'Branch name is required',
  }

  useEffect(() => {
    api.get(`/onboarding/form/${token}`).then(res => {
      setLinkInfo(res.data)
      if (res.data.existingData) {
        const d = res.data.existingData
        setForm({
          fullName: d.fullName || '',
          dateOfBirth: d.dateOfBirth ? d.dateOfBirth.split('T')[0] : '',
          gender: d.gender || '',
          bloodGroup: d.bloodGroup || '',
          dateOfJoining: d.dateOfJoining ? d.dateOfJoining.split('T')[0] : '',
          aadhaarNumber: d.aadhaarNumber || '',
          phoneNumber: d.phoneNumber || '',
          altPhoneNumber: d.altPhoneNumber || '',
          personalEmail: d.personalEmail || '',
          emergencyContactName: d.emergencyContactName || '',
          emergencyRelationship: d.emergencyRelationship || '',
          emergencyContactPhone: d.emergencyContactPhone || '',
          permanentAddress: d.permanentAddress || '',
          contactAddress: d.contactAddress || '',
          bankAccountNumber: d.bankAccountNumber || '',
          bankIfscCode: d.bankIfscCode || '',
          bankBranchName: d.bankBranchName || ''
        })
      } else if (res.data.employeeName) {
        setForm(f => ({ ...f, fullName: res.data.employeeName || '' }))
      }
      if (res.data.employeeEmail) {
        setForm(f => ({ ...f, personalEmail: res.data.employeeEmail || '' }))
      }
      setLoading(false)
    }).catch(err => {
      setError(err.response?.data?.error || 'Invalid or expired link')
      setLoading(false)
    })
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Validate required fields
    const errors: Record<string, string> = {}
    for (const [key, msg] of Object.entries(requiredFields)) {
      if (!form[key as keyof typeof form]?.trim()) errors[key] = msg
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      // Scroll to first error
      const firstKey = Object.keys(errors)[0]
      document.querySelector(`[data-field="${firstKey}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setSaving(true)
    try {
      const submitData = { ...form }
      if (sameAddress) submitData.contactAddress = form.permanentAddress
      await api.post(`/onboarding/form/${token}`, submitData)
      setSubmitted(true)
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to submit form')
    }
    setSaving(false)
  }

  const fieldClass = (key: string) =>
    `input${fieldErrors[key] ? ' border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-400">Loading...</p>
    </div>
  )

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-yellow-500" />
        </div>
        <h2 className="font-bold text-navy-900 text-xl mb-2">Link Error</h2>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  )

  if (submitted || (linkInfo?.hasSubmission && linkInfo?.submissionStatus === 'pending')) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        <h2 className="font-bold text-navy-900 text-xl mb-2">Form Submitted!</h2>
        <p className="text-gray-500">Thank you. Your onboarding form has been submitted successfully. HR will review it and get back to you if any changes are needed.</p>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#071829] px-6 py-5">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <img src="/New Inkcredible Logo.webp" alt="Inkcredible Studios" className="h-10 object-contain" />
          <div>
            <h1 className="text-white font-bold text-lg">Employee Onboarding</h1>
            <p className="text-orange-400 text-xs">Inkcredible Studios</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        {/* HR Note if returned */}
        {linkInfo?.hrNote && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6">
            <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-1 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Note from HR</p>
            <p className="text-sm text-yellow-800">{linkInfo.hrNote}</p>
          </div>
        )}

        <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-sm p-6 sm:p-8 space-y-6">
          <div>
            <h2 className="font-bold text-navy-900 text-xl">Personal Information</h2>
            <p className="text-gray-400 text-sm mt-1">Please fill in all required fields accurately</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Details */}
            <div className="space-y-4">
              <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 pb-2">Personal Details</p>
              <div>
                <label className="label">Full Name *</label>
                <input className={fieldClass('fullName')} data-field="fullName" value={form.fullName} onChange={e => set('fullName', e.target.value)} placeholder="As per Aadhaar card" />
                <FieldError errors={fieldErrors} name="fullName" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Date of Birth *</label>
                  <input className={fieldClass('dateOfBirth')} data-field="dateOfBirth" type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} />
                  <FieldError errors={fieldErrors} name="dateOfBirth" />
                </div>
                <div>
                  <label className="label">Gender *</label>
                  <select className={fieldClass('gender')} data-field="gender" value={form.gender} onChange={e => set('gender', e.target.value)}>
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Blood Group</label>
                  <select className="input" value={form.bloodGroup} onChange={e => set('bloodGroup', e.target.value)}>
                    <option value="">Select</option>
                    {BLOOD_GROUPS.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Date of Joining *</label>
                  <input className="input" type="date" value={form.dateOfJoining} onChange={e => set('dateOfJoining', e.target.value)} required />
                </div>
              </div>
              <div>
                <label className="label">Aadhaar Number *</label>
                <input className="input" value={form.aadhaarNumber} onChange={e => set('aadhaarNumber', e.target.value)} required placeholder="XXXX XXXX XXXX" maxLength={14} />
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 pb-2">Contact Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Phone Number *</label>
                  <input className="input" value={form.phoneNumber} onChange={e => set('phoneNumber', e.target.value)} required placeholder="+91 XXXXX XXXXX" />
                </div>
                <div>
                  <label className="label">Alternative Phone</label>
                  <input className="input" value={form.altPhoneNumber} onChange={e => set('altPhoneNumber', e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div>
                <label className="label">Personal Email *</label>
                <input className="input" type="email" value={form.personalEmail} onChange={e => set('personalEmail', e.target.value)} required placeholder="your@email.com" />
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="space-y-4">
              <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 pb-2">Emergency Contact</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Contact Name *</label>
                  <input className="input" value={form.emergencyContactName} onChange={e => set('emergencyContactName', e.target.value)} required placeholder="Full name" />
                </div>
                <div>
                  <label className="label">Relationship *</label>
                  <select className="input" value={form.emergencyRelationship} onChange={e => set('emergencyRelationship', e.target.value)} required>
                    <option value="">Select</option>
                    <option value="Parent">Parent</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Emergency Contact Number *</label>
                <input className="input" value={form.emergencyContactPhone} onChange={e => set('emergencyContactPhone', e.target.value)} required placeholder="+91 XXXXX XXXXX" />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 pb-2">Address</p>
              <div>
                <label className="label">Permanent Address *</label>
                <textarea className="input resize-none" rows={3} value={form.permanentAddress} onChange={e => set('permanentAddress', e.target.value)} required placeholder="Full permanent address" />
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="checkbox" checked={sameAddress} onChange={e => setSameAddress(e.target.checked)} className="w-4 h-4 accent-orange-500" />
                  <span className="text-sm text-gray-600">Contact address is same as permanent address</span>
                </label>
                {!sameAddress && (
                  <div>
                    <label className="label">Contact Address</label>
                    <textarea className="input resize-none" rows={3} value={form.contactAddress} onChange={e => set('contactAddress', e.target.value)} placeholder="Current contact address (if different)" />
                  </div>
                )}
              </div>
            </div>

            {/* Bank Details */}
            <div className="space-y-4">
              <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 pb-2">Bank Account Details</p>
              <div>
                <label className="label">Account Number *</label>
                <input className="input" value={form.bankAccountNumber} onChange={e => set('bankAccountNumber', e.target.value)} required placeholder="Bank account number" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">IFSC Code *</label>
                  <input className="input uppercase" value={form.bankIfscCode} onChange={e => set('bankIfscCode', e.target.value.toUpperCase())} required placeholder="XXXX0000000" maxLength={11} />
                </div>
                <div>
                  <label className="label">Branch Name *</label>
                  <input className="input" value={form.bankBranchName} onChange={e => set('bankBranchName', e.target.value)} required placeholder="Branch name" />
                </div>
              </div>
            </div>

            <button type="submit" disabled={saving}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors text-sm">
              {saving ? 'Submitting...' : 'Submit Onboarding Form'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          This link expires on {linkInfo?.expiresAt ? new Date(linkInfo.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
        </p>
      </div>
    </div>
  )
}